import { useCallback, useEffect, useState, useRef } from "react";
import ClientLayout from "../components/ClientLayout";
import { FileText, Printer, Download, ArrowLeft, Wifi } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { useNavigate, useParams } from "react-router-dom";
import { useCMS } from "../context/CMSContext";

export default function ClientSOADetail() {
  const navigate = useNavigate();
  const { soaId } = useParams();
  const [loading, setLoading] = useState(true);
  const [soa, setSOA] = useState(null);
  const printRef = useRef();
  const { settings } = useCMS();
  const companyName = settings?.company_name || "ISP Manager";

  useEffect(() => {
    fetchSOA();
  }, [soaId]);

  const fetchSOA = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/soa/${soaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSOA(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/client/login");
        return;
      }
      toast.error("Failed to fetch SOA details");
      navigate("/client/soa");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const winPrint = window.open(
      "",
      "",
      "left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0",
    );

    winPrint.document.write(`
      <html>
        <head>
          <title>SOA - ${soa?.soa_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #2563eb; padding-bottom: 20px; }
            .logo { font-size: 24px; font-weight: bold; color: #2563eb; margin-bottom: 5px; }
            .company { font-size: 14px; color: #64748b; }
            .soa-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .soa-info div { flex: 1; }
            .label { font-size: 12px; color: #64748b; margin-bottom: 2px; }
            .value { font-size: 14px; font-weight: 600; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
            th { background-color: #f8fafc; font-weight: 600; font-size: 12px; text-transform: uppercase; }
            .amount { text-align: right; }
            .total-row { font-weight: bold; font-size: 16px; background-color: #f1f5f9; }
            .total-row td { border-top: 2px solid #2563eb; }
            .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #64748b; }
            .payment { color: #16a34a; }
            .charge { color: #dc2626; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <div class="footer">
            <p>This is a computer-generated document. No signature required.</p>
            <p>For inquiries, contact support@isp.com</p>
          </div>
        </body>
      </html>
    `);

    winPrint.document.close();
    winPrint.focus();
    setTimeout(() => {
      winPrint.print();
      winPrint.close();
    }, 250);
  };

  // Download SOA PDF
  const downloadSOAPDF = async (soaId, soaNumber) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/soa/${soaId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `SOA_${soaNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const downloadPDF = () => {
    // For now, use the print functionality - in production, could use a PDF library
    handlePrint();
    toast.success("Use the Save as PDF option in the print dialog");
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </ClientLayout>
    );
  }

  if (!soa) return null;

  // Date formatting helper - use MMM-dd-yyyy format (e.g., Jan-01-2026)
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      // If already in MMM-dd-yyyy format, return as is
      if (/^[A-Z][a-z]{2}-\d{2}-\d{4}/.test(dateStr))
        return dateStr.split(" ")[0];
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[date.getMonth()];
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <ClientLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/client/soa")}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Statement of Account
              </h1>
              <p className="text-slate-600">{soa.soa_number}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {/* <Button variant="outline" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button> */}
            <Button onClick={() => downloadSOAPDF(soa.id, soa.soa_number)}>
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>

        {/* Printable Content */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div ref={printRef} className="p-8">
            {/* SOA Header */}
            <div className="header text-center border-b-2 border-blue-600 pb-6 mb-6">
              <div className="logo flex items-center justify-center gap-2 text-2xl font-bold text-blue-600">
                <Wifi className="w-8 h-8" />
                {companyName}
              </div>
              <div className="company text-slate-500">
                Internet Service Provider
              </div>
              <h2 className="text-xl font-bold mt-4 text-slate-900">
                STATEMENT OF ACCOUNT
              </h2>
            </div>

            {/* Client & SOA Info */}
            <div className="soa-info grid grid-cols-2 gap-8 mb-8">
              <div>
                <div className="mb-4">
                  <p className="label text-xs text-slate-500">Account Number</p>
                  <p className="value text-lg font-bold">
                    {soa.account_number}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="label text-xs text-slate-500">Account Name</p>
                  <p className="value font-semibold">{soa.client_name}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="mb-4">
                  <p className="label text-xs text-slate-500">SOA Number</p>
                  <p className="value font-bold">{soa.soa_number}</p>
                </div>
                <div className="mb-4">
                  <p className="label text-xs text-slate-500">Billing Period</p>
                  <p className="value">
                    {formatDate(soa.billing_period_start)} -{" "}
                    {formatDate(soa.billing_period_end)}
                  </p>
                </div>
                <div>
                  <p className="label text-xs text-slate-500">Due Date</p>
                  <p className="value font-bold text-red-600">
                    {formatDate(soa.due_date)}
                  </p>
                </div>
              </div>
            </div>

            {/* Line Items Table */}
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase border-b-2 border-slate-200">
                    Description
                  </th>
                  <th className="p-3 text-right text-xs font-semibold text-slate-600 uppercase border-b-2 border-slate-200">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {soa.line_items.map((item, index) => (
                  <tr key={index} className="border-b border-slate-100">
                    <td className="p-3 text-slate-700">{item.description}</td>
                    <td
                      className={`p-3 text-right font-medium ${item.type === "payment" ? "text-emerald-600" : "text-slate-900"}`}
                    >
                      {item.type === "payment" ? "-" : ""}₱
                      {Math.abs(item.amount).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row bg-slate-100">
                  <td className="p-4 font-bold text-slate-900 border-t-2 border-blue-600">
                    TOTAL AMOUNT DUE
                  </td>
                  <td className="p-4 text-right font-bold text-xl text-blue-600 border-t-2 border-blue-600">
                    ₱{soa.total_amount_due.toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>

            {/* Summary */}
            <div className="mt-8 p-4 bg-slate-50 rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-slate-500">Previous Balance</p>
                  <p className="font-bold text-slate-900">
                    ₱{soa.previous_balance.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Current Charges</p>
                  <p className="font-bold text-slate-900">
                    ₱{soa.current_charges.toLocaleString()}
                  </p>
                </div>
                {/* <div>
                  <p className="text-xs text-slate-500">Total Payments</p>
                  <p className="font-bold text-emerald-600">₱{soa.total_payments.toLocaleString()}</p>
                </div> */}
                <div>
                  <p className="text-xs text-slate-500">Amount Due</p>
                  <p className="font-bold text-lg text-red-600">
                    ₱{soa.total_amount_due.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
}
